import React, { useEffect, useMemo, useState } from 'react';
import AnnotationPreview from '../../components/AnnotationPreview/AnnotationPreview.component';
import { SelectableTable } from '../../components/SelectableTable/SelectableTable.component';
import { SelectableTableColumn } from '../../components/SelectableTable/SelectableTable.types';
import { getAnnotationTypeLabel } from '../../components/SystemSettings/AnnotationDefinitionsManager/utils';
import { AnnotationDefinition } from '../../models/annotation.types';
import {
  AnnotationAssignmentStoreDefinition,
  AnnotationsTableItem,
  IAnnotationAssigment,
  IAnnotationStoreAssigment,
} from './AnnotationsAssignment.types';
import { useStyles } from './AnnotationsAssignment.styles';
import { ButtonOne } from '../../components/ButtonOne/ButtonOne.component';
import { useLayoutContext } from '../../Context/Layout.context';
import { storeExamSheetAnnotations } from '../../services/exams/examSheet/examSheet.service';
import { AxiosResponse } from 'axios';
import { LeavePrompt } from '../../components/Prompt/LeavePrompt/LeavePrompt.component';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import SwitchOne from '../../components/SwitchOne/SwitchOne.component';

interface AnnotationAssigmentEditorProps {
  selectedCriterionsIds: string[];
  assignedAnnotations: IAnnotationAssigment[];
  annotationDefinitions: AnnotationDefinition[];
  onChange?: () => void;
  onClose?: () => void;
  onSave?: () => void;
}

const AnnotationAssigmentEditor: React.FC<AnnotationAssigmentEditorProps> = (props) => {
  const classes = useStyles();
  const { openAlert, showLoader } = useLayoutContext();
  const { t } = useTranslation();
  const [oryginalAssignments, setOryginalAssignments] = useState<IAnnotationAssigment[]>(props.assignedAnnotations);
  const [assignments, setAssignment] = useState<IAnnotationAssigment[]>(oryginalAssignments);
  const [tableRows, setTableRows] = useState<AnnotationsTableItem[]>([]);
  const [editorMode, setEditorMode] = useState<'view' | 'edit'>(oryginalAssignments.length ? 'view' : 'edit');
  const [cancelEditInfoModalOpen, setCancelEditInfoModalOpen] = useState<boolean>(false);

  const columns: SelectableTableColumn[] = [
    {
      id: 'icon',
      type: 'text',
      title: 'Ikona',
      filterType: null,
    },
    {
      id: 'type',
      type: 'text',
      title: 'Typ',
      filterType: null,
    },
    {
      id: 'name',
      type: 'text',
      title: 'Nazwa',
      filterType: null,
    },
    {
      id: 'description',
      type: 'text',
      title: 'Opis',
      filterType: null,
    },
    {
      id: 'calculatingAnnotation',
      type: 'children',
      title: 'Adnotacje do obliczeń',
      filterType: null,
    },
    {
      id: 'gradingAnnotation',
      type: 'children',
      title: 'Adnotacje do oceniania',
      filterType: null,
    },
  ];

  const handleCalculatingAnnotationToggle = (annotationId: string) => {
    const foundAssignment = assignments.find((item: IAnnotationAssigment) => item.id === annotationId);

    if (foundAssignment) {
      setAssignment((prevState) => prevState.filter((item: IAnnotationAssigment) => item.id !== annotationId));
    } else {
      setAssignment((prevState) => [
        ...prevState,
        { id: annotationId, calculationAnnotation: true, gradingAnnotation: false },
      ]);
    }
    props.onChange && props.onChange();
  };

  const handleGradingAnnotationToggle = (annotationId: string) => {
    setAssignment((prevState) =>
      prevState.map((item: IAnnotationAssigment) => {
        if (item.id == annotationId) {
          return { ...item, gradingAnnotation: !item.gradingAnnotation };
        } else return item;
      }),
    );
    props.onChange && props.onChange();
  };

  const handleSaveAssignment = () => {
    const annotationsForAllCriterions: IAnnotationStoreAssigment[] = assignments.map(
      (assignment: IAnnotationAssigment) => {
        return {
          annotationDefinitionId: assignment.id,
          gradingAnnotation: assignment.gradingAnnotation,
          calculationAnnotation: true,
        };
      },
    );

    const scoringSchemaDtos: AnnotationAssignmentStoreDefinition[] = props.selectedCriterionsIds.map(
      (criterionsId: string) => {
        return {
          scoringSchemaId: criterionsId,
          annotations: annotationsForAllCriterions,
        };
      },
    );

    showLoader(true);
    storeExamSheetAnnotations(scoringSchemaDtos)
      .then(
        (response: AxiosResponse<boolean>) => {
          if (response.data) {
            openAlert('success');
            props.onClose && props.onClose();
            props.onSave && props.onSave();
          } else {
            openAlert('save_error');
          }
        },
        () => openAlert('save_error'),
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const filteredAnnotationDefinitions = useMemo(() => {
    const annotationDefinitionsForAssignedAnnotations = oryginalAssignments.length
      ? props.annotationDefinitions.filter((data: AnnotationDefinition) => {
          return !!oryginalAssignments.find((item: IAnnotationAssigment) => item.id === data.id);
        })
      : [];
    return annotationDefinitionsForAssignedAnnotations.length
      ? annotationDefinitionsForAssignedAnnotations
      : props.annotationDefinitions;
  }, [oryginalAssignments, props.annotationDefinitions]);

  useEffect(() => {
    setEditorMode(oryginalAssignments.length ? 'view' : 'edit');
  }, [oryginalAssignments.length]);

  useEffect(() => {
    const rows = filteredAnnotationDefinitions
      .sort((a, b) => a.annotationType - b.annotationType)
      .map((data: AnnotationDefinition) => {
        const foundAssignment = assignments.find((item: IAnnotationAssigment) => item.id === data.id);
        const isCalculatingAnnotation = !!foundAssignment;
        const isGradingAnnotation = foundAssignment && foundAssignment.gradingAnnotation;

        return {
          id: data.id,
          icon: (
            <div style={{ margin: '5px 0 5px 0' }}>
              <AnnotationPreview svg={data.svg} />
            </div>
          ),
          type: getAnnotationTypeLabel(data.annotationType),
          name: data.name,
          description: data.description,
          calculatingAnnotation: (
            <div>
              <SwitchOne
                disableRipple
                color={'primary'}
                disabled={editorMode === 'view'}
                checked={isCalculatingAnnotation}
                onChange={() => handleCalculatingAnnotationToggle(data.id)}
                inputProps={{ 'aria-label': 'Adnotacja do obliczeń' }}
              />
            </div>
          ),
          gradingAnnotation: isCalculatingAnnotation ? (
            <div>
              <SwitchOne
                disableRipple
                color={'primary'}
                disabled={editorMode === 'view'}
                checked={isGradingAnnotation}
                onChange={() => handleGradingAnnotationToggle(data.id)}
                inputProps={{ 'aria-label': 'Adnotacja do oceny' }}
              />
            </div>
          ) : (
            <></>
          ),
        };
      });
    setTableRows(rows);
  }, [oryginalAssignments, assignments, editorMode]);

  const isDataChanged = useMemo(() => {
    const isEqual = _.isEqual(
      props.assignedAnnotations.sort((a, b) => a.id.localeCompare(b.id)),
      assignments.sort((a, b) => a.id.localeCompare(b.id)),
    );
    return !isEqual;
  }, [oryginalAssignments, assignments]);

  return (
    <>
      <SelectableTable<AnnotationsTableItem>
        columns={columns}
        rows={tableRows}
        isMultiChoice={false}
        withoutEmptyMessage={true}
        noOverflowInitial={true}
        grayBodyCell={true}
        hideCheckboxMenu={true}
        caption="Adnotacje"
      />

      <div className={classes.actionButtons}>
        <ButtonOne
          color="primary"
          variant="outlined"
          onClick={() => {
            if (!isDataChanged) {
              setCancelEditInfoModalOpen(true);
            } else {
              setCancelEditInfoModalOpen(false);
              props.onClose && props.onClose();
            }
          }}
          style={{ margin: '5px' }}
        >
          Anuluj
        </ButtonOne>
        {oryginalAssignments.length == 0 && (
          <ButtonOne
            disabled={!isDataChanged}
            color="primary"
            variant="contained"
            onClick={handleSaveAssignment}
            style={{ margin: '5px' }}
          >
            Zapisz
          </ButtonOne>
        )}
        {oryginalAssignments.length > 0 && (
          <ButtonOne
            color="primary"
            variant="contained"
            onClick={() => setOryginalAssignments([])}
            style={{ margin: '5px' }}
          >
            Edytuj
          </ButtonOne>
        )}
      </div>
      {cancelEditInfoModalOpen && (
        <LeavePrompt
          isOpen={true}
          onConfirm={() => {
            setCancelEditInfoModalOpen(false);
            props.onClose && props.onClose();
          }}
          onCancel={() => {
            setCancelEditInfoModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default AnnotationAssigmentEditor;
